import { BackofficePrestationType, BuyableItemType, Prisma } from "@prisma/client";
import {
  AdvancedStatisticsBuyableItemHandler,
  AiImageCreditsBuyableItemHandler,
  BadgeBuyableItemHandler,
  CashRegisterCreditsBuyableItemHandler,
  EquipmentDeliveryFeeBuyableItemHandler,
  EventCustomAppearanceBuyableItemHandler,
  EventCustomPdfAppearanceBuyableItemHandler,
  EventEquipmentRequestBuyableItemHandler,
  ExhibitorAdvancedStatisticsBuyableItemHandler,
  ExhibitorProfilesPack1CreditsBuyableItemHandler,
  ExhibitorProfilesPack2CreditsBuyableItemHandler,
  ExhibitorProfilesPack3CreditsBuyableItemHandler,
  ExhibitorPromotionBuyableItemHandler,
  ExportExhibitorsModuleBuyableItemHandler,
  ExportProgramModuleBuyableItemHandler,
  PromotionBuyableItemHandler,
  RefundBuyableItemHandler,
  TicketBuyableItemHandler,
} from "./buyable-item-handlers";
import { computablePricingValues } from "./pricing.types";

export const validatorBuyableItemValues = Prisma.validator<Prisma.BuyableItemDefaultArgs>()({
  select: {
    buyableType: true,
    ticketTypeId: true,
    badgeTypeId: true,
    advancedStatisticsEventId: true,
    eventCustomPdfAppearanceEventId: true,
    eventCustomAppearanceEventId: true,
    exhibitorAdvancedStatisticsEventId: true,
    exhibitorAdvancedStatisticsStructureId: true,
    exhibitorProfilesPack1CreditsOrganiserId: true,
    exhibitorProfilesPack2CreditsOrganiserId: true,
    exhibitorProfilesPack3CreditsOrganiserId: true,
    aiImageCreditsOrganiserId: true,
    cashRegisterCreditsEventId: true,
    promotionEventId: true,
    promotionDates: true,
    exhibitorPromotionEventId: true,
    exportExhibitorsModuleEventId: true,
    exportProgramModuleEventId: true,
    eventEquipmentRequest: { select: { id: true } },
    feeEventEquipmentRequest: { select: { id: true } },
    creditNoteId: true,
  },
});

export type ValidatorBuyableItem = Prisma.BuyableItemGetPayload<typeof validatorBuyableItemValues>;

export const invalidatorBuyableItemValues = Prisma.validator<Prisma.BuyableItemDefaultArgs>()({
  select: {
    buyableType: true,
    ticketTypeId: true,
    badgeTypeId: true,
    eventCustomAppearanceEventId: true,
  },
});

export type InvalidatorBuyableItem = Prisma.BuyableItemGetPayload<
  typeof invalidatorBuyableItemValues
>;

export const buyableItemValuesForEventName = Prisma.validator<Prisma.BuyableItemDefaultArgs>()({
  select: {
    buyableType: true,
    advancedStatisticsEvent: { select: { name: true, id: true } },
    eventCustomPdfAppearanceEvent: { select: { name: true, id: true } },
    eventCustomAppearanceEvent: { select: { name: true, id: true } },
    exhibitorAdvancedStatisticsEvent: { select: { name: true, id: true } },
    exportExhibitorsModuleEvent: { select: { name: true, id: true } },
    exportProgramModuleEvent: { select: { name: true, id: true } },
    promotionEvent: { select: { name: true, id: true } },
    exhibitorPromotionEvent: { select: { name: true, id: true } },
    exhibitorProfilesPack1CreditsOrganiser: { select: { id: true } },
    exhibitorProfilesPack2CreditsOrganiser: { select: { id: true } },
    exhibitorProfilesPack3CreditsOrganiser: { select: { id: true } },
    aiImageCreditsOrganiser: { select: { id: true } },
    cashRegisterCreditsEvent: { select: { name: true, id: true } },
    eventEquipmentRequest: {
      select: { event: { select: { name: true, id: true } } },
    },
    feeEventEquipmentRequest: {
      select: { event: { select: { name: true, id: true } } },
    },
    ticketType: { select: { event: { select: { id: true, name: true } } } },
    badgeType: { select: { event: { select: { id: true, name: true } } } },
    creditNote: {
      select: {
        creditNoteItems: {
          select: {
            orderItem: {
              select: {
                ticket: {
                  select: {
                    ticketType: {
                      select: {
                        event: { select: { id: true, name: true } },
                      },
                    },
                  },
                },
                badge: {
                  select: {
                    badgeType: {
                      select: {
                        event: { select: { id: true, name: true } },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  },
});

export type BuyableItemForEventName = Prisma.BuyableItemGetPayload<
  typeof buyableItemValuesForEventName
>;

export const accountingBuyableItemValues = Prisma.validator<Prisma.BuyableItemDefaultArgs>()({
  include: {
    advancedStatisticsEvent: true,
    eventCustomPdfAppearanceEvent: true,
    eventCustomAppearanceEvent: true,
    exhibitorAdvancedStatisticsEvent: true,
    promotionEvent: true,
    exhibitorPromotionEvent: true,
    exhibitorProfilesPack1CreditsOrganiser: true,
    exhibitorProfilesPack2CreditsOrganiser: true,
    exhibitorProfilesPack3CreditsOrganiser: true,
    aiImageCreditsOrganiser: true,
    cashRegisterCreditsEvent: true,
    eventEquipmentRequest: {
      include: {
        event: true,
      },
    },
    feeEventEquipmentRequest: {
      include: {
        event: true,
      },
    },
    exportExhibitorsModuleEvent: true,
    exportProgramModuleEvent: true,
    ticketType: {
      include: {
        event: true,
      },
    },
    badgeType: {
      include: {
        event: true,
      },
    },
    creditNote: {
      select: {
        id: true,
        reference: true,
        creditNoteItems: {
          select: {
            orderItem: {
              select: {
                finalTotal: true,
                ticket: {
                  select: {
                    code: true,
                    ticketType: {
                      select: {
                        name: true,
                        event: {
                          select: {
                            id: true,
                            name: true,
                          },
                        },
                      },
                    },
                  },
                },
                badge: {
                  select: {
                    code: true,
                    badgeType: {
                      select: {
                        name: true,
                        event: {
                          select: {
                            id: true,
                            name: true,
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  },
});

export const completeBuyableItemValues = Prisma.validator<Prisma.BuyableItemDefaultArgs>()({
  select: {
    id: true,
    buyableType: true,
    ticketTypeId: true,
    badgeTypeId: true,
    badgeType: {
      select: {
        quantityLeft: true,
        id: true,
        name: true,
        pricing: computablePricingValues,
        groupMinSize: true,
        validityDates: true,
        event: {
          select: {
            id: true,
            name: true,
            organiser: {
              select: {
                id: true,
                admin: {
                  select: {
                    firstName: true,
                    lastName: true,
                  },
                },
              },
            },
            ticketing: {
              select: {
                id: true,
                availabilityEndingDate: true,
                availabilityStartingDate: true,
              },
            },
          },
        },
      },
    },
    ticketType: {
      select: {
        quantityLeft: true,
        activityId: true,
        validityDates: true,
        isAvailableOnDisabledTicketing: true,
        activity: {
          select: {
            structureId: true,
            structure: {
              select: {
                id: true,
                admin: {
                  select: {
                    firstName: true,
                    lastName: true,
                    businessName: true,
                  },
                },
              },
            },
          },
        },
        id: true,
        name: true,
        pricing: computablePricingValues,
        groupMinSize: true,
        saleStartDate: true,
        saleEndDate: true,
        userType: true,
        buyingLimitForUserType: true,
        hasGroupVisibilityAndBuyingLimits: true,
        groupLimits: {
          select: {
            groupId: true,
            maxTicketsPerUser: true,
          },
        },
        event: {
          select: {
            id: true,
            name: true,
            organiser: {
              select: {
                id: true,
                admin: {
                  select: {
                    firstName: true,
                    lastName: true,
                    businessName: true,
                  },
                },
              },
            },
            ticketing: {
              select: {
                id: true,
                availabilityEndingDate: true,
                availabilityStartingDate: true,
              },
            },
          },
        },
      },
    },
    advancedStatisticsEvent: {
      select: {
        id: true,
        name: true,
      },
    },
    eventCustomPdfAppearanceEvent: {
      select: {
        id: true,
        name: true,
      },
    },
    eventCustomAppearanceEvent: {
      select: {
        id: true,
        name: true,
      },
    },
    exhibitorAdvancedStatisticsEvent: {
      select: {
        id: true,
        name: true,
      },
    },
    exhibitorAdvancedStatisticsStructure: true,
    promotionEvent: {
      select: {
        id: true,
        name: true,
      },
    },
    promotionDates: true,
    exhibitorPromotionEvent: {
      select: {
        id: true,
        name: true,
      },
    },
    exhibitorProfilesPack1CreditsOrganiser: {
      select: {
        id: true,
      },
    },
    exhibitorProfilesPack2CreditsOrganiser: {
      select: {
        id: true,
      },
    },
    exhibitorProfilesPack3CreditsOrganiser: {
      select: {
        id: true,
      },
    },
    aiImageCreditsOrganiser: {
      select: {
        id: true,
      },
    },
    cashRegisterCreditsEvent: {
      select: {
        id: true,
        name: true,
      },
    },
    exportExhibitorsModuleEvent: {
      select: {
        id: true,
        name: true,
      },
    },
    exportProgramModuleEvent: {
      select: {
        id: true,
        name: true,
      },
    },
    eventEquipmentRequestId: true,
    eventEquipmentRequest: {
      select: {
        id: true,
        event: {
          select: {
            id: true,
            name: true,
            organiserId: true,
          },
        },
        eventEquipmentAllocations: {
          select: {
            id: true,
            count: true,
            eventEquipment: {
              select: {
                id: true,
                name: true,
              },
            },
            dates: true,
          },
        },
      },
    },
    feeEventEquipmentRequestId: true,
    feeEventEquipmentRequest: {
      select: {
        id: true,
        event: {
          select: {
            id: true,
            name: true,
            organiserId: true,
          },
        },
        eventEquipmentAllocations: {
          select: {
            id: true,
            count: true,
            eventEquipment: {
              select: {
                id: true,
                name: true,
              },
            },
            dates: true,
          },
        },
      },
    },
    creditNoteId: true,
    creditNote: {
      select: {
        id: true,
        reference: true,
        creditNoteItems: {
          select: {
            orderItem: {
              select: {
                finalTotal: true,
                vatRate: true,
                ticket: {
                  select: {
                    code: true,
                    ticketType: {
                      select: {
                        name: true,
                        event: {
                          select: {
                            id: true,
                            name: true,
                          },
                        },
                      },
                    },
                  },
                },
                badge: {
                  select: {
                    code: true,
                    badgeType: {
                      select: {
                        name: true,
                        event: {
                          select: {
                            id: true,
                            name: true,
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  },
});

export type CompleteBuyableItem = Prisma.BuyableItemGetPayload<typeof completeBuyableItemValues>;

export type PartialCompleteBuyableItem = CompleteBuyableItem extends infer T
  ? {
      [K in keyof T]: null extends T[K] ? T[K] | undefined : T[K];
    }
  : never;

export const getEntryTypeBuyableItemDefaultArgs = <
  T extends Prisma.TicketType$buyableItemArgs | Prisma.BadgeType$buyableItemArgs,
>(
  args: T,
) => {
  return {
    ...args,
    where: {
      ...args.where,
      deletedAt: null,
    },
    take: 1,
  } satisfies Omit<T, "take" | "where"> & {
    where: T["where"] & {
      deletedAt: null;
    };
    take: 1;
  };
};

export const isBuyableItemType = (type: string): type is BuyableItemType =>
  Object.values(BuyableItemType).includes(type as BuyableItemType);

export const buyableTicketingTypes = [BuyableItemType.ticket, BuyableItemType.badge];

export const buyablePrestationTypes = Object.values(BackofficePrestationType).filter(
  (type): type is BackofficePrestationType =>
    !buyableTicketingTypes.includes(type as (typeof buyableTicketingTypes)[number]),
);

export type BuyableTicketingType = (typeof buyableTicketingTypes)[number];

export const isBuyableTicketingType = (type: BuyableItemType): type is BuyableTicketingType =>
  buyableTicketingTypes.includes(type as BuyableTicketingType);

export const isBuyablePrestationType = (type: BuyableItemType): type is BackofficePrestationType =>
  buyablePrestationTypes.includes(type as BackofficePrestationType);

export type BuyableItemEvent = { id: string; name: string };
export type BuyableItemOrganiser = { id: string };

export type PartialBuyableItem = Partial<Omit<CompleteBuyableItem, "buyableType">> & {
  buyableType: BuyableItemType;
};

export type BuyableItemHandler = {
  readonly displayName: string;
  readonly isCumulative?: boolean;
  readonly accounting: {
    generalAccountCode: string;
    invoiceLabel: string;
  };
  getCompleteItemName?: (item: PartialBuyableItem) => string | null;
  getGroupKey(item: PartialBuyableItem): string;
  parseGroupKeyAsWhere(groupKey: string): Prisma.BuyableItemWhereInput;
  isGroupKey(groupKey: string): boolean;
  isValid(item: object): boolean;
  findEquivalent(item: object): Prisma.BuyableItemWhereInput;
  create(item: object): Omit<Prisma.BuyableItemCreateInput, "buyableType">;
  update?(item: object): Prisma.BuyableItemUpdateInput;
  getPricingQuery(item: PartialBuyableItem): Prisma.PricingFindFirstArgs;
  getEvent?: (item: any) => BuyableItemEvent;
  getOrganiser?: (item: any) => BuyableItemOrganiser;
  getLinkedGroupKeys?(groupKey: string): string[];
  getEventSearchFilter?(searchText: string): Prisma.BuyableItemWhereInput;
};

export const buyableItemHandlerMap = {
  [BuyableItemType.ticket]: TicketBuyableItemHandler,
  [BuyableItemType.badge]: BadgeBuyableItemHandler,
  [BuyableItemType.advancedStatistics]: AdvancedStatisticsBuyableItemHandler,
  [BuyableItemType.eventCustomPdfAppearance]: EventCustomPdfAppearanceBuyableItemHandler,
  [BuyableItemType.eventCustomAppearance]: EventCustomAppearanceBuyableItemHandler,
  [BuyableItemType.exhibitorAdvancedStatistics]: ExhibitorAdvancedStatisticsBuyableItemHandler,
  [BuyableItemType.exhibitorProfilesPack1Credits]: ExhibitorProfilesPack1CreditsBuyableItemHandler,
  [BuyableItemType.exhibitorProfilesPack2Credits]: ExhibitorProfilesPack2CreditsBuyableItemHandler,
  [BuyableItemType.exhibitorProfilesPack3Credits]: ExhibitorProfilesPack3CreditsBuyableItemHandler,
  [BuyableItemType.cashRegisterCredits]: CashRegisterCreditsBuyableItemHandler,
  [BuyableItemType.promotion]: PromotionBuyableItemHandler,
  [BuyableItemType.exhibitorPromotion]: ExhibitorPromotionBuyableItemHandler,
  [BuyableItemType.eventEquipmentRequest]: EventEquipmentRequestBuyableItemHandler,
  [BuyableItemType.exportExhibitorsModule]: ExportExhibitorsModuleBuyableItemHandler,
  [BuyableItemType.exportProgramModule]: ExportProgramModuleBuyableItemHandler,
  [BuyableItemType.aiImageCredits]: AiImageCreditsBuyableItemHandler,
  [BuyableItemType.refund]: RefundBuyableItemHandler,
  [BuyableItemType.equipmentDeliveryFee]: EquipmentDeliveryFeeBuyableItemHandler,
} as const satisfies Record<BuyableItemType, BuyableItemHandler>;

export type BuyableItemHandlerMap = typeof buyableItemHandlerMap;

export type BuyableItemHandlerInstance = BuyableItemHandlerMap[BuyableItemType];

export type BuyableItemCreationType = {
  [K in keyof BuyableItemHandlerMap]: BuyableItemHandlerMap[K] extends {
    create: (item: infer TItem) => unknown;
  }
    ? {
        buyableType: K;
      } & TItem
    : {
        buyableType: K;
      } & { id: string };
}[keyof BuyableItemHandlerMap];

export type BuyableItemUpdateType = {
  [K in keyof BuyableItemHandlerMap]: BuyableItemHandlerMap[K] extends {
    update: (item: infer TItem) => unknown;
  }
    ? {
        buyableType: K;
      } & Partial<TItem>
    : {
        buyableType: K;
      } & { item: never };
}[keyof BuyableItemHandlerMap];

export type BuyableItemFindEquivalentType = {
  [K in keyof BuyableItemHandlerMap]: BuyableItemHandlerMap[K] extends {
    findEquivalent: (item: infer TItem) => Prisma.BuyableItemWhereInput;
  }
    ? {
        buyableType: K;
      } & TItem
    : {
        buyableType: K;
      } & { id: string };
}[keyof BuyableItemHandlerMap];

export type BuyableItemIsValidType = {
  [K in keyof BuyableItemHandlerMap]: BuyableItemHandlerMap[K] extends {
    isValid: (item: infer TItem) => boolean;
  }
    ? {
        buyableType: K;
      } & TItem
    : {
        buyableType: K;
      } & { id: string };
}[keyof BuyableItemHandlerMap];

export type BuyableItemWithEventType = {
  [K in keyof BuyableItemHandlerMap]: BuyableItemHandlerMap[K] extends {
    getEvent: (item: infer TItem) => BuyableItemEvent;
  }
    ? {
        buyableType: K;
      } & TItem
    : never;
}[keyof BuyableItemHandlerMap];

export type BuyableItemWithOrganiserType = {
  [K in keyof BuyableItemHandlerMap]: BuyableItemHandlerMap[K] extends {
    getOrganiser: (item: infer TItem) => BuyableItemOrganiser;
  }
    ? {
        buyableType: K;
      } & TItem
    : never;
}[keyof BuyableItemHandlerMap];

type _BuyableItemCreationConstraint = BuyableItemCreationType extends BuyableItemFindEquivalentType
  ? true
  : never;
const _constraint: _BuyableItemCreationConstraint = true;

export {
  AdvancedStatisticsBuyableItemHandler,
  AiImageCreditsBuyableItemHandler,
  BadgeBuyableItemHandler,
  CashRegisterCreditsBuyableItemHandler,
  EquipmentDeliveryFeeBuyableItemHandler,
  EventCustomPdfAppearanceBuyableItemHandler,
  EventEquipmentRequestBuyableItemHandler,
  ExhibitorAdvancedStatisticsBuyableItemHandler,
  ExhibitorProfilesPack1CreditsBuyableItemHandler,
  ExhibitorProfilesPack2CreditsBuyableItemHandler,
  ExhibitorProfilesPack3CreditsBuyableItemHandler,
  ExhibitorPromotionBuyableItemHandler,
  ExportExhibitorsModuleBuyableItemHandler,
  PromotionBuyableItemHandler,
  TicketBuyableItemHandler,
};
